import { useEffect, useState, type ReactNode } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { HealthProvider } from '../api/health-context'
import { useHealth } from '../api/use-health'
import { ModeProvider } from './ModeProvider'
import { ModeToggle, ModeToggleCompact } from './ModeToggle'
import { ThemeToggle } from './ThemeToggle'

function ModeBadge() {
  const { health, loading, error } = useHealth()
  if (loading) return <span className="text-text-dim text-xs">denetleniyor…</span>
  if (error || !health)
    return <span className="text-malignant text-xs">sunucuya ulaşılamıyor</span>
  const cls = health.mode === 'live'
    ? 'bg-benign/20 text-benign border-benign/40'
    : 'bg-accent/20 text-accent border-accent/40'
  const label = health.mode === 'live' ? 'canlı' : 'demo'
  return (
    <span
      className={`text-[10px] sm:text-xs uppercase tracking-wider px-1.5 sm:px-2 py-0.5 rounded border whitespace-nowrap ${cls}`}
      title={health.mode === 'live' ? 'canlı mod' : 'demo mod'}
    >
      {label}
    </span>
  )
}

interface NavItem {
  to: string
  label: string
  disabled?: boolean
}

function useNavItems(): NavItem[] {
  const { health } = useHealth()
  const liveOn = health?.mode === 'live'
  return [
    { to: '/', label: 'Ana sayfa' },
    { to: '/cases', label: 'Demo vakalar' },
    { to: '/doktor', label: 'Doktor modu' },
    { to: '/hakkinda', label: 'Hakkında' },
    { to: '/tesekkur', label: 'Teşekkürler' },
    { to: '/teknik-detaylar', label: 'Teknik detaylar' },
    { to: '/upload', label: 'Görüntü yükle', disabled: !liveOn },
  ]
}

function DesktopNav() {
  const items = useNavItems()
  return (
    <nav className="hidden lg:flex gap-1">
      {items.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          end={it.to === '/'}
          onClick={(e) => it.disabled && e.preventDefault()}
          title={it.disabled ? 'Yalnızca canlı modda' : undefined}
          className={({ isActive }) =>
            [
              'px-3 py-1 rounded text-sm whitespace-nowrap',
              it.disabled
                ? 'text-text-dim/50 cursor-not-allowed'
                : isActive
                ? 'bg-surface-2 text-text'
                : 'text-text-dim hover:text-text hover:bg-surface-2',
            ].join(' ')
          }
        >
          {it.label}
        </NavLink>
      ))}
    </nav>
  )
}

function MobileNav({ open, onClose }: { open: boolean; onClose: () => void }) {
  const items = useNavItems()
  const location = useLocation()

  // Close on route change
  useEffect(() => {
    onClose()
  }, [location.pathname]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null
  return (
    <nav className="lg:hidden border-t border-border bg-surface flex flex-col">
      {items.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          end={it.to === '/'}
          onClick={(e) => it.disabled && e.preventDefault()}
          className={({ isActive }) =>
            [
              'px-4 py-3 text-sm border-b border-border/60 last:border-b-0',
              it.disabled
                ? 'text-text-dim/50'
                : isActive
                ? 'bg-surface-2 text-text'
                : 'text-text-dim hover:bg-surface-2 hover:text-text',
            ].join(' ')
          }
        >
          {it.label}
          {it.disabled && (
            <span className="ml-2 text-[10px] uppercase tracking-wider text-text-dim/60">
              canlı modda
            </span>
          )}
        </NavLink>
      ))}
    </nav>
  )
}

function Hamburger({ open, onClick }: { open: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={open ? 'Menüyü kapat' : 'Menüyü aç'}
      aria-expanded={open}
      onClick={onClick}
      className="lg:hidden p-2 -mr-2 text-text-dim hover:text-text rounded hover:bg-surface-2"
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="w-5 h-5"
      >
        {open ? (
          <>
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </>
        ) : (
          <>
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </>
        )}
      </svg>
    </button>
  )
}

export function Layout({ children }: { children: ReactNode }) {
  const [menuOpen, setMenuOpen] = useState(false)

  return (
    <ModeProvider>
    <HealthProvider>
      <div className="min-h-screen flex flex-col">
        <header className="border-b border-border bg-surface sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2.5 sm:py-3 flex items-center gap-2 sm:gap-3">
            <Link
              to="/"
              className="flex items-baseline gap-2 sm:gap-3 no-underline min-w-0 flex-1"
            >
              <span className="font-semibold text-base sm:text-lg text-text">Sezi</span>
              <span className="text-xs text-text-dim hidden sm:inline truncate">
                Yapay zeka destekli meme MR analizi
              </span>
              <span className="text-[10px] text-text-dim sm:hidden truncate">
                Meme MR analizi
              </span>
            </Link>
            <DesktopNav />
            <ModeToggle />
            <ModeToggleCompact />
            <ThemeToggle />
            <ModeBadge />
            <Hamburger open={menuOpen} onClick={() => setMenuOpen((v) => !v)} />
          </div>
          <MobileNav open={menuOpen} onClose={() => setMenuOpen(false)} />
          <div className="bg-warn/15 text-warn text-[11px] sm:text-xs px-3 sm:px-4 py-1.5 text-center border-t border-warn/20 leading-snug">
            Araştırma prototipi. Klinik veya tanı amaçlı bir cihaz değildir; çıktıları
            tıbbi tavsiye yerine geçmez.
          </div>
        </header>
        <main className="flex-1 max-w-7xl w-full mx-auto px-3 sm:px-4 py-4 sm:py-6">
          {children}
        </main>
        <footer className="border-t border-border text-center text-xs text-text-dim py-3 px-3 sm:px-4 leading-snug space-y-1.5">
          <p>
            Sezi, Başkent Üniversitesi bünyesinde proje çalışması olarak
            geliştirilmiştir. Açık veri kümeleri ve açık kaynak kütüphaneler üzerine
            inşa edilmiştir.
          </p>
          <p className="flex flex-wrap justify-center gap-x-3 gap-y-1">
            <Link to="/hakkinda" className="text-accent hover:underline">
              Hakkında
            </Link>
            <span className="text-text-dim/40">·</span>
            <Link to="/tesekkur" className="text-accent hover:underline">
              Teşekkürler
            </Link>
            <span className="text-text-dim/40">·</span>
            <Link to="/teknik-detaylar" className="text-accent hover:underline">
              Teknik detaylar
            </Link>
          </p>
        </footer>
      </div>
    </HealthProvider>
    </ModeProvider>
  )
}
